import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { copyTextToClipboard } from "@faire/web--source/common/copyTextToClipboard";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { optimizeImageUrlFillSquare } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Button } from "@faire/web--source/slate/Button";
import { Link } from "@faire/web--source/slate/icons/foundation/Link";
import { Column, HorizontalLine, Row } from "@faire/web--source/slate/Layout";
import { Modal } from "@faire/web--source/slate/Modal";
import { Spacer } from "@faire/web--source/slate/spacing";
import { createToast } from "@faire/web--source/slate/Toast";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackRetailerTeamManagementCopyLinkButtonClick } from "@faire/web-api--source/events/retailerTeamManagement/click/copyLinkButton";
import { trackRetailerTeamSharingInviteUserLeanFlowClick } from "@faire/web-api--source/events/retailerTeamSharing/click/inviteUserLeanFlow";
import { ShareableItemType } from "@faire/web-api--source/indigofair/retailer/ShareableItemType";
import React from "react";
import { SilenceChildComponent } from "@faire/retailer/components/Silence/SilenceChildComponent";
import { InviteTeamMemberSection } from "@faire/retailer/views/multi-user/Invite/InviteTeamMemberSection";
import { TOAST_AUTO_DISMISS_TIME_MS } from "@faire/retailer/views/multi-user/Share/consts";
var ITEM_IMAGE_SIZE = 96;
export var ShareWithTeamGTMModal = function(param) {
    var modalType = param.modalType, image = param.image, name = param.name, subText = param.subText, onClose = param.onClose, silenceGTMMilestone = param.silenceGTMMilestone, viewCountBeforeSilencingGTM = param.viewCountBeforeSilencingGTM, onInvite = param.onInvite, pageName = param.pageName;
    var isSSR = useIsSSR();
    var viewport = useViewport();
    var strictLocalize = useStrictLocalization().strictLocalize;
    var createLinkCopiedToast = function() {
        createToast(/*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "e0OktD",
            defaultMessage: [
                {
                    type: 0,
                    value: "Link copied successfully"
                }
            ]
        }), {
            icon: "foundation:CheckOutline",
            timeout: TOAST_AUTO_DISMISS_TIME_MS
        });
    };
    var handleCopyLink = function() {
        trackRetailerTeamManagementCopyLinkButtonClick(pageName);
        var window = getWindowOrThrow();
        copyTextToClipboard(window.location.href);
        createLinkCopiedToast();
        onClose();
    };
    var inviteSectionDescription = modalType === ShareableItemType.PRODUCT ? strictLocalize({
        id: "ZxoAZW",
        defaultMessage: [
            {
                type: 0,
                value: "Or invite a team member to join your account so they can view full product details."
            }
        ]
    }) : strictLocalize({
        id: "anS3+b",
        defaultMessage: [
            {
                type: 0,
                value: "Or invite a team member to join your account so they can view full brand details."
            }
        ]
    });
    return /*#__PURE__*/ _jsxs(Modal, {
        padding: "88px 24px 24px 24px",
        width: viewport.isTabletAndAbove ? "420px" : undefined,
        onClose: onClose,
        align: "stretch",
        "data-test-id": "share-with-team-gtm-modal",
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "pageHeaderSerifRegular",
                children: modalType === ShareableItemType.PRODUCT ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "RPANM+",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Share product"
                        }
                    ]
                }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "s7lPKw",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Share brand"
                        }
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: "2x"
            }),
            /*#__PURE__*/ _jsx(Typography, {
                color: Core.text.hover,
                children: modalType === ShareableItemType.PRODUCT ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "vlpz3F",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Send a preview of this product to anyone."
                        }
                    ]
                }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "XhGWAo",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Send a preview of this brand to anyone."
                        }
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "2x"
            }),
            /*#__PURE__*/ _jsxs(Row, {
                align: "center",
                children: [
                    /*#__PURE__*/ _jsx("img", {
                        src: optimizeImageUrlFillSquare({
                            url: image === null || image === void 0 ? void 0 : image.url
                        }, isSSR, ITEM_IMAGE_SIZE),
                        width: ITEM_IMAGE_SIZE,
                        height: ITEM_IMAGE_SIZE
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: "2x"
                    }),
                    /*#__PURE__*/ _jsxs(Column, {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                children: name
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: 4
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                color: Core.text.subdued,
                                children: subText
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "2x"
            }),
            /*#__PURE__*/ _jsx(Row, {
                children: /*#__PURE__*/ _jsxs(Button, {
                    variant: "tertiary",
                    onClick: handleCopyLink,
                    children: [
                        /*#__PURE__*/ _jsx(Link, {}),
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: 4
                        }),
                        /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "eqg+27",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Copy preview link"
                                }
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(SilenceChildComponent, {
                milestone: silenceGTMMilestone,
                maxViewCount: viewCountBeforeSilencingGTM,
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "3x"
                    }),
                    /*#__PURE__*/ _jsx(HorizontalLine, {}),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "3x"
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        children: inviteSectionDescription
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "2x"
                    }),
                    /*#__PURE__*/ _jsx(InviteTeamMemberSection, {
                        onInviteSent: onInvite,
                        onInviteButtonClick: function() {
                            return trackRetailerTeamSharingInviteUserLeanFlowClick(pageName);
                        }
                    })
                ]
            })
        ]
    });
};
